import type ImageCarousel from '@naturehouse/design-system/components/organisms/image-carousel/ImageCarousel';
import { ImageCarouselEvent } from '@naturehouse/design-system/components/organisms/image-carousel/ImageCarousel';
import BigQuery from '../../common/BigQuery';

export enum ImageCarouselTrackingEvent {
    IMAGE_CAROUSEL_INTERACTION = 'image_carousel_interaction'
}

export default class ImageCarouselTrackingManager {
    static #instance: ImageCarouselTrackingManager | null = null;

    public static getInstance(
        imageCarousel: ImageCarousel,
        houseId: string
    ): ImageCarouselTrackingManager {
        if (this.#instance === null || this.#instance.houseId !== houseId) {
            this.#instance = new ImageCarouselTrackingManager(imageCarousel, houseId);
        }

        return this.#instance;
    }

    public static destroyInstance(houseId: string): void {
        if (this.#instance === null || this.#instance.houseId !== houseId) {
            return;
        }

        this.#instance = null;
    }

    readonly #imageCarouselEvents = [
        ImageCarouselEvent.NEXT_BUTTON_CLICK,
        ImageCarouselEvent.PREV_BUTTON_CLICK,
        ImageCarouselEvent.NEXT_SWIPE_ACTION,
        ImageCarouselEvent.PREV_SWIPE_ACTION,
        ImageCarouselEvent.THUMBNAIL_CLICK,
        ImageCarouselEvent.SLIDE_CHANGED
    ];

    #hasInteracted: boolean = false;

    private constructor(
        private readonly imageCarousel: ImageCarousel,
        private readonly houseId: string
    ) {
        this.#setEventListeners();
    }

    readonly #setEventListeners = (): void => {
        for (const event of this.#imageCarouselEvents) {
            this.imageCarousel.addEventListener(event, this.#setInteractionEvent);
        }
    };

    readonly #removeEventListeners = (): void => {
        for (const event of this.#imageCarouselEvents) {
            this.imageCarousel.removeEventListener(event, this.#setInteractionEvent);
        }
    };

    readonly #setInteractionEvent = async (): Promise<void> => {
        if (this.#hasInteracted) {
            this.#removeEventListeners();
            return;
        }

        this.#hasInteracted = true;
        await BigQuery.track({
            eventName: ImageCarouselTrackingEvent.IMAGE_CAROUSEL_INTERACTION,
            eventParams: [
                {
                    eventKey: 'houseId',
                    eventValue: this.houseId
                }
            ]
        });
    };
}
